import React, { useState } from 'react';
import { ItemSearchResult } from '../../models/ItemSearchResult';
import { createGroupEdit } from '../../service/GroupEditService';

type Props = {
  items: ItemSearchResult[];
  onClose: () => void;
  onSaved: () => void;
};

interface RowState {
  newQty: number;
  error: string | null;
}

const GroupEditModal: React.FC<Props> = ({ items, onClose, onSaved }) => {
  const [rows, setRows] = useState<RowState[]>(
    items.map(item => ({ newQty: item.quantity, error: null }))
  );
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);

  // ── Σ rule: compute totals ─────────────────────────────────────────────
  const originalTotal    = items.reduce((s, item) => s + item.quantity, 0);
  const newTotal         = rows.reduce((s, r) => s + r.newQty, 0);
  const sumNotBalanced   = newTotal !== originalTotal;

  const handleQtyChange = (idx: number, raw: string) => {
    const parsed = parseInt(raw, 10);
    const newQty = isNaN(parsed) ? 0 : Math.max(0, parsed);
    setRows(prev => {
      const next = [...prev];
      next[idx] = { newQty, error: null };
      return next;
    });
  };

  const hasChanges = items.some((item, idx) => rows[idx].newQty !== item.quantity);
  const canSave    = !sumNotBalanced && hasChanges && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setApiError(null);
    setWarnings([]);
    setSaving(true);
    try {
      const response = await createGroupEdit({
        comment: comment.trim() || undefined,
        edits: items.map((item, idx) => ({
          sessionId: item.sessionId,
          itemId:    item.itemId,
          newQty:    rows[idx].newQty,
        })),
      });
      if (response.warnings && response.warnings.length > 0) {
        setWarnings(response.warnings);
        // Show warnings briefly then close
        setTimeout(() => onSaved(), 2500);
      } else {
        onSaved();
      }
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Failed to save. Please try again.';
      setApiError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-lg font-semibold text-gray-800">
            <i className="fas fa-layer-group mr-2 text-blue-500"></i>
            Edit Items Together
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">

          {/* Comment */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Comment <span className="text-gray-400">(optional — shown in each item's history)</span>
            </label>
            <textarea
              rows={2}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 resize-none"
              placeholder="e.g. Confirmed with warehouse — units arrived in different delivery"
              value={comment}
              onChange={e => setComment(e.target.value)}
            />
          </div>

          {/* Σ warning */}
          {sumNotBalanced && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">
              <i className="fas fa-ban mr-2"></i>
              {newTotal > originalTotal ? (
                <>
                  <strong>Cannot save:</strong> the total quantity would <strong>increase</strong> by{' '}
                  <strong>{newTotal - originalTotal}</strong> unit(s) (was {originalTotal}, now {newTotal}).
                  Adding new quantity must be done directly in the session view.
                </>
              ) : (
                <>
                  <strong>Cannot save:</strong> the total quantity would <strong>decrease</strong> by{' '}
                  <strong>{originalTotal - newTotal}</strong> unit(s) (was {originalTotal}, now {newTotal}).
                  All removed quantity must be redistributed to another item in the group.
                </>
              )}
            </div>
          )}

          {/* Non-blocking warnings from API */}
          {warnings.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-1">
              <p className="text-xs font-semibold text-amber-700 mb-1">
                <i className="fas fa-exclamation-triangle mr-1"></i>Saved with warnings:
              </p>
              {warnings.map((w, i) => (
                <p key={i} className="text-xs text-amber-700">{w}</p>
              ))}
            </div>
          )}

          {/* Item table */}
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  <th className="px-3 py-2">Part Number</th>
                  <th className="px-3 py-2">Description</th>
                  <th className="px-3 py-2">Session</th>
                  <th className="px-3 py-2 text-center">Expected</th>
                  <th className="px-3 py-2 text-center">Was</th>
                  <th className="px-3 py-2 text-center">New Qty</th>
                  <th className="px-3 py-2 text-center">Gap / Excess</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => {
                  const was     = item.quantity;
                  const newQty  = rows[idx].newQty;
                  const changed = newQty !== was;
                  const gap     = item.shipQtd - newQty;    // positive = still missing
                  const excess  = newQty - item.shipQtd;    // positive = still over

                  return (
                    <tr key={item.itemId} className={`border-b transition ${changed ? 'bg-blue-50/60' : ''}`}>
                      <td className="px-3 py-2 font-mono text-xs">{item.partNumber}</td>
                      <td className="px-3 py-2 max-w-xs truncate text-xs text-gray-600" title={item.description}>
                        {item.description}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <span className="inline-block px-2 py-0.5 rounded bg-green-100 text-green-700 text-xs">
                          {item.sessionTitle}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-center text-gray-600">{item.shipQtd}</td>
                      <td className="px-3 py-2 text-center">
                        <span className={`font-medium ${changed ? 'line-through text-gray-400' : 'text-gray-700'}`}>
                          {was}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          min={0}
                          className={`w-20 border rounded-lg px-2 py-1 text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-300 ${
                            changed ? 'border-blue-400 font-medium' : ''
                          }`}
                          value={newQty}
                          onChange={e => handleQtyChange(idx, e.target.value)}
                        />
                      </td>
                      <td className="px-3 py-2 text-center text-xs">
                        {newQty === item.shipQtd ? (
                          <span className="text-green-600 font-medium">
                            <i className="fas fa-check mr-1"></i>OK
                          </span>
                        ) : gap > 0 ? (
                          <span className="text-red-600">−{gap} missing</span>
                        ) : (
                          <span className="text-amber-600">+{excess} over</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {/* Σ totals footer */}
              <tfoot>
                <tr className="border-t bg-gray-50">
                  <td colSpan={4} className="px-3 py-2 text-xs font-semibold text-gray-500 text-right">
                    Total quantity:
                  </td>
                  <td className="px-3 py-2 text-center text-xs font-semibold text-gray-500">
                    {originalTotal}
                  </td>
                  <td className="px-3 py-2 text-center text-xs font-semibold">
                    <span className={newTotal > originalTotal ? 'text-red-600' : newTotal < originalTotal ? 'text-amber-600' : 'text-gray-700'}>
                      {newTotal}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-center text-xs font-semibold">
                    {newTotal !== originalTotal && (
                      <span className={newTotal > originalTotal ? 'text-red-600' : 'text-amber-600'}>
                        {newTotal > originalTotal ? `+${newTotal - originalTotal} ⚠` : `−${originalTotal - newTotal}`}
                      </span>
                    )}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {apiError && <p className="text-red-600 text-sm">{apiError}</p>}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition text-sm"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 gradient-bg text-white rounded-lg hover:opacity-90 transition text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!canSave}
            title={sumNotBalanced ? (newTotal > originalTotal ? 'Total quantity cannot increase' : 'All removed quantity must be redistributed') : !hasChanges ? 'No changes to save' : undefined}
          >
            {saving ? 'Saving…' : 'Save All Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GroupEditModal;
